import { getEnvConfig } from '@hbs-us/contracts';

import { getAppEnv } from '../../lib/env.js';
import type { ReportsSearch } from './searchParams.js';

/**
 * Build the credentialed download URLs for the reports export/print paths (D-06).
 *
 * Export CSV + Print View must TRIGGER the api-host download paths
 * (`GET /api/v1/reports/export.csv`, `GET /api/v1/reports/print`) — the export
 * logic is NOT rebuilt client-side (the server owns CSV serialization and the
 * Browser-Rendering print/PDF engine). These are absolute, cross-origin URLs
 * pointed at the api host from `@hbs-us/contracts getEnvConfig().domains.api` —
 * NEVER hardcoded (threat T-20-12: a leaked host would credentialed-fetch from
 * the wrong origin).
 *
 * The links carry the SAME filter the table is showing (`range`/`start`/`end`),
 * so the export matches the on-screen report. They are opened with the user's
 * cookies (CSV via `<a download>`, print via `window.open(_, _blank)`); both are
 * safe GET reads (CARVE-07 read-only — no CSRF token needed).
 */

function apiBaseUrl(): string {
  return `https://${getEnvConfig(getAppEnv()).domains.api}`;
}

/** Append the active filter to a search-params object (custom dates only when range=custom). */
function filterParams(search: ReportsSearch): URLSearchParams {
  const params = new URLSearchParams();
  params.set('range', search.range);
  if (search.range === 'custom') {
    if (search.start) params.set('start', search.start);
    if (search.end) params.set('end', search.end);
  }
  return params;
}

export interface ReportExportLinks {
  /** Credentialed CSV download (`<a href download>`), api host. */
  csvUrl: string;
  /** Credentialed print/PDF view (`window.open(url, '_blank', 'noreferrer')`), api host. */
  printUrl: string;
}

export function buildReportExportLinks(search: ReportsSearch): ReportExportLinks {
  const base = apiBaseUrl();
  const query = filterParams(search).toString();
  return {
    csvUrl: `${base}/api/v1/reports/export.csv?${query}`,
    printUrl: `${base}/api/v1/reports/print?${query}`,
  };
}
